import {
	getEquipmentQueryKey,
	getEquipmentQueryOptions,
	listInspectionsQueryOptions,
} from "@packages/api-client/hooks/equipmentController";
import { useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
	AlertTriangle,
	Calendar,
	ClipboardCheck,
	Edit,
	Gauge,
	MapPin,
	Upload,
	Wrench,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useListMedia } from "@/api/media";
import { ImageUploadDialog } from "@/components/media/ImageUploadDialog";
import { MediaGallery } from "@/components/media/MediaGallery";
import { OfflineUnavailable } from "@/components/OfflineUnavailable";
import { PageHeader } from "@/components/PageHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useDateFormatter } from "@/hooks/useDateFormatter";
import { findOfflineEquipmentRow } from "@/hooks/useEquipmentListData";
import { useFarmScope } from "@/hooks/useFarmScope";
import { useOnlineStatus } from "@/hooks/useOnlineStatus";
import { loadUnlessOffline } from "@/lib/offline/loadUnlessOffline";
import { EquipmentStatusBadge } from "../../-components/EquipmentStatusBadge";
import { MaintenanceDueIndicator } from "../../-components/MaintenanceDueIndicator";
import { EquipmentDetailOffline } from "./-components/EquipmentDetailOffline";

export const Route = createFileRoute(
	"/_auth/app/_app/org/$orgId/equipment/fleet/$equipmentId/",
)({
	staticData: { offline: "ready" },
	loader: ({ context, params }) =>
		loadUnlessOffline(async () => {
			await context.queryClient.ensureQueryData(
				getEquipmentQueryOptions(params.orgId, params.equipmentId),
			);
		}),
	component: EquipmentDetailRoute,
});

function EquipmentDetailRoute() {
	const { orgId, equipmentId } = Route.useParams();
	const online = useOnlineStatus();
	const queryClient = useQueryClient();
	const scope = useFarmScope();

	if (!online) {
		const cachedFull = queryClient.getQueryData(
			getEquipmentQueryKey(orgId, equipmentId),
		);
		if (!cachedFull) {
			// The equipment establishment isn't part of this route's path (fleet
			// detail is a global route), so the row lookup uses the top-bar farm
			// scope (mirrors the planning task detail pattern).
			const row = findOfflineEquipmentRow(
				queryClient,
				orgId,
				scope.kind === "single" ? scope.establishmentId : undefined,
				equipmentId,
			);
			// Prepared dataset row → offline read-only view; nothing at all →
			// the same "prepare in the Offline Center" panel the guard used to show.
			return row ? (
				<EquipmentDetailOffline row={row} />
			) : (
				<OfflineUnavailable variant="preparable" />
			);
		}
	}

	return <EquipmentDetailPage />;
}

function EquipmentDetailPage() {
	const { orgId, equipmentId } = Route.useParams();
	const { t } = useTranslation();
	const { formatDateOnly } = useDateFormatter();
	const navigate = Route.useNavigate();

	const { data: equipment } = useSuspenseQuery(
		getEquipmentQueryOptions(orgId, equipmentId),
	);

	const { data: inspectionsData } = useSuspenseQuery(
		listInspectionsQueryOptions(orgId, equipmentId),
	);

	const { data: mediaData } = useListMedia(orgId, {
		entityType: "EquipMgmtEquipment",
		entityId: equipmentId,
	});
	const imagesCount = mediaData?.data?.length ?? 0;

	if (!equipment) {
		return (
			<div className="flex h-full items-center justify-center">
				<p className="text-base-content/70">{t("equipment.fleet.notFound")}</p>
			</div>
		);
	}

	const makeModel = [equipment.make, equipment.model].filter(Boolean).join(" ");

	return (
		<div className="flex flex-col gap-4 h-full">
			<PageHeader
				title={equipment.name}
				showBackButton
				actions={
					<div className="flex gap-2">
						<Button asChild variant="outline" size="sm">
							<Link
								to="/app/org/$orgId/equipment/fleet/$equipmentId/inspect"
								params={{ orgId, equipmentId }}
							>
								<ClipboardCheck className="h-4 w-4 mr-1" />
								{t("equipment.inspection.startInspection")}
							</Link>
						</Button>
						<Button asChild variant="outline" size="sm">
							<Link
								to="/app/org/$orgId/equipment/breakdown-report"
								params={{ orgId }}
								search={{ equipmentId }}
							>
								<AlertTriangle className="h-4 w-4 mr-1" />
								{t("equipment.breakdown.reportBreakdown")}
							</Link>
						</Button>
						<ImageUploadDialog
							orgId={orgId}
							entityType="EquipMgmtEquipment"
							entityId={equipmentId}
							trigger={
								<Button variant="outline" size="sm">
									<Upload className="h-4 w-4 mr-1" />
									{t("media.upload.button")}
								</Button>
							}
						/>
						<Button
							variant="outline"
							size="sm"
							onClick={() =>
								navigate({
									to: "/app/org/$orgId/equipment/fleet/$equipmentId/edit",
									params: { orgId, equipmentId },
								})
							}
						>
							<Edit className="h-4 w-4 mr-1" />
							{t("common.edit")}
						</Button>
					</div>
				}
			/>

			{/* Profile Card */}
			<Card>
				<CardContent className="pt-5 pb-4 space-y-4">
					{/* Badges + Key Metrics */}
					<div className="flex flex-wrap items-center gap-x-5 gap-y-2">
						<EquipmentStatusBadge status={equipment.status} />
						<Badge variant="outline">
							{t(
								`equipment.categories.${equipment.category}`,
								equipment.category,
							)}
						</Badge>
						<Badge variant="secondary">{equipment.assetId}</Badge>

						<span className="h-4 w-px bg-border" />

						<div className="flex items-center gap-1.5 text-sm">
							<Gauge className="h-3.5 w-3.5 text-muted-foreground" />
							<span className="text-muted-foreground">
								{t("equipment.fleet.hours")}:
							</span>
							<span className="font-medium">
								{equipment.currentHours.toLocaleString()}h
							</span>
						</div>

						{equipment.location && (
							<div className="flex items-center gap-1.5 text-sm">
								<MapPin className="h-3.5 w-3.5 text-muted-foreground" />
								<span className="font-medium">{equipment.location.name}</span>
							</div>
						)}

						{equipment.nextPmDue && (
							<div className="flex items-center gap-1.5 text-sm">
								<Wrench className="h-3.5 w-3.5 text-muted-foreground" />
								<MaintenanceDueIndicator
									dueDate={equipment.nextPmDue.dueDate}
									dueHours={equipment.nextPmDue.dueHours}
								/>
							</div>
						)}
					</div>

					{/* Detail Grid */}
					<dl className="grid gap-x-6 gap-y-2 grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 text-sm">
						{makeModel && (
							<div>
								<dt className="text-muted-foreground">
									{t("equipment.fleet.makeModel")}
								</dt>
								<dd className="font-medium">{makeModel}</dd>
							</div>
						)}
						{equipment.year && (
							<div>
								<dt className="text-muted-foreground">
									{t("equipment.fleet.year")}
								</dt>
								<dd className="font-medium">{equipment.year}</dd>
							</div>
						)}
						{equipment.serialNumber && (
							<div>
								<dt className="text-muted-foreground">
									{t("equipment.form.serialNumber")}
								</dt>
								<dd className="font-medium">{equipment.serialNumber}</dd>
							</div>
						)}
						{equipment.fuelType && (
							<div>
								<dt className="text-muted-foreground">
									{t("equipment.form.fuelType")}
								</dt>
								<dd className="font-medium">
									{t(
										`equipment.fuelTypes.${equipment.fuelType}`,
										equipment.fuelType,
									)}
								</dd>
							</div>
						)}
						{equipment.horsepower && (
							<div>
								<dt className="text-muted-foreground">
									{t("equipment.form.horsepower")}
								</dt>
								<dd className="font-medium">{equipment.horsepower} HP</dd>
							</div>
						)}
						<div>
							<dt className="text-muted-foreground">
								{t("equipment.form.ownershipType")}
							</dt>
							<dd className="font-medium">
								{t(
									`equipment.ownershipTypes.${equipment.ownershipType}`,
									equipment.ownershipType,
								)}
							</dd>
						</div>
						{equipment.purchaseDate && (
							<div>
								<dt className="text-muted-foreground">
									{t("equipment.form.purchaseDate")}
								</dt>
								<dd className="font-medium">
									{formatDateOnly(new Date(equipment.purchaseDate).getTime())}
								</dd>
							</div>
						)}
						{equipment.dealer && (
							<div>
								<dt className="text-muted-foreground">
									{t("equipment.form.dealer")}
								</dt>
								<dd className="font-medium">{equipment.dealer}</dd>
							</div>
						)}
						{equipment.notes && (
							<div className="col-span-2 sm:col-span-3 lg:col-span-4">
								<dt className="text-muted-foreground">{t("common.notes")}</dt>
								<dd className="font-medium">{equipment.notes}</dd>
							</div>
						)}
					</dl>

					{/* Stats Row */}
					<div className="flex flex-wrap gap-4 pt-2 border-t border-base-300">
						<div className="text-sm">
							<span className="text-muted-foreground">
								{t("equipment.detail.totalTasks")}:
							</span>{" "}
							<span className="font-medium">{equipment.stats.totalTasks}</span>
							{equipment.stats.openTasks > 0 && (
								<Badge variant="secondary" className="ml-1 text-xs">
									{equipment.stats.openTasks} {t("common.open")}
								</Badge>
							)}
						</div>
						<div className="text-sm">
							<span className="text-muted-foreground">
								{t("equipment.detail.inspections")}:
							</span>{" "}
							<span className="font-medium">
								{equipment.stats.totalInspections}
							</span>
						</div>
						{equipment.stats.lastInspectionDate && (
							<div className="flex items-center gap-1 text-sm">
								<Calendar className="h-3.5 w-3.5 text-muted-foreground" />
								<span className="text-muted-foreground">
									{t("equipment.detail.lastInspection")}:
								</span>{" "}
								<span className="font-medium">
									{formatDateOnly(
										new Date(equipment.stats.lastInspectionDate).getTime(),
									)}
								</span>
							</div>
						)}
					</div>
				</CardContent>
			</Card>

			{/* Tabs */}
			<Tabs defaultValue="inspections" className="w-full flex-1">
				<TabsList>
					<TabsTrigger value="inspections">
						{t("equipment.tabs.inspections")} (
						{(inspectionsData?.data ?? []).length})
					</TabsTrigger>
					<TabsTrigger value="details">
						{t("equipment.tabs.details")}
					</TabsTrigger>
					<TabsTrigger value="images">
						{t("media.gallery.title")} ({imagesCount})
					</TabsTrigger>
				</TabsList>

				<TabsContent value="inspections" className="mt-4">
					{(inspectionsData?.data ?? []).length === 0 ? (
						<div className="card bg-base-100 shadow-md">
							<div className="card-body flex flex-col items-center justify-center py-12">
								<ClipboardCheck className="h-12 w-12 text-base-content/30 mb-4" />
								<p className="text-base-content/70 text-center">
									{t("equipment.inspection.list.empty")}
								</p>
								<Button asChild className="mt-4">
									<Link
										to="/app/org/$orgId/equipment/fleet/$equipmentId/inspect"
										params={{ orgId, equipmentId }}
									>
										<ClipboardCheck className="h-4 w-4 mr-2" />
										{t("equipment.inspection.startInspection")}
									</Link>
								</Button>
							</div>
						</div>
					) : (
						<div className="card bg-base-100 shadow-md">
							<div className="card-body">
								<div className="overflow-x-auto">
									<table className="table table-zebra w-full">
										<thead>
											<tr>
												<th>{t("common.date")}</th>
												<th>{t("equipment.inspection.hourMeter")}</th>
												<th>{t("equipment.inspection.result")}</th>
												<th>{t("equipment.inspection.submittedBy")}</th>
											</tr>
										</thead>
										<tbody>
											{(inspectionsData?.data ?? []).map(
												(inspection: {
													id: string;
													createdAt: string;
													hourMeterReading: number;
													defectsCount?: number;
													criticalDefectsCount?: number;
													submitter?: { name: string } | null;
												}) => (
													<tr key={inspection.id}>
														<td>
															{formatDateOnly(
																new Date(inspection.createdAt).getTime(),
															)}
														</td>
														<td>{inspection.hourMeterReading}h</td>
														<td>
															{(inspection.criticalDefectsCount ?? 0) > 0 ? (
																<Badge
																	variant="destructive"
																	className="text-xs"
																>
																	{t("equipment.inspection.criticalDefects", {
																		count: inspection.criticalDefectsCount,
																	})}
																</Badge>
															) : (inspection.defectsCount ?? 0) > 0 ? (
																<Badge variant="secondary" className="text-xs">
																	{t("equipment.inspection.defects", {
																		count: inspection.defectsCount,
																	})}
																</Badge>
															) : (
																<Badge
																	variant="outline"
																	className="text-xs text-success"
																>
																	{t("equipment.inspection.pass")}
																</Badge>
															)}
														</td>
														<td>{inspection.submitter?.name ?? "-"}</td>
													</tr>
												),
											)}
										</tbody>
									</table>
								</div>
							</div>
						</div>
					)}
				</TabsContent>

				<TabsContent value="details" className="mt-4">
					<Card>
						<CardContent className="pt-5">
							<dl className="grid gap-x-6 gap-y-3 grid-cols-2 sm:grid-cols-3 text-sm">
								{equipment.pmIntervalHours && (
									<div>
										<dt className="text-muted-foreground">
											{t("equipment.form.pmIntervalHours")}
										</dt>
										<dd className="font-medium">
											{equipment.pmIntervalHours}h
										</dd>
									</div>
								)}
								{equipment.pmIntervalDays && (
									<div>
										<dt className="text-muted-foreground">
											{t("equipment.form.pmIntervalDays")}
										</dt>
										<dd className="font-medium">
											{equipment.pmIntervalDays} {t("common.days")}
										</dd>
									</div>
								)}
								{equipment.pmTemplateName && (
									<div>
										<dt className="text-muted-foreground">
											{t("equipment.form.pmTemplateName")}
										</dt>
										<dd className="font-medium">{equipment.pmTemplateName}</dd>
									</div>
								)}
								{equipment.purchasePrice && (
									<div>
										<dt className="text-muted-foreground">
											{t("equipment.form.purchasePrice")}
										</dt>
										<dd className="font-medium">
											${equipment.purchasePrice.toLocaleString()}
										</dd>
									</div>
								)}
								{equipment.warrantyExpiry && (
									<div>
										<dt className="text-muted-foreground">
											{t("equipment.form.warrantyExpiry")}
										</dt>
										<dd className="font-medium">
											{formatDateOnly(
												new Date(equipment.warrantyExpiry).getTime(),
											)}
										</dd>
									</div>
								)}
								<div>
									<dt className="text-muted-foreground">
										{t("equipment.detail.totalMaintenanceCost")}
									</dt>
									<dd className="font-medium">
										${equipment.stats.totalMaintenanceCost.toLocaleString()}
									</dd>
								</div>
								<div>
									<dt className="text-muted-foreground">
										{t("common.createdAt")}
									</dt>
									<dd className="font-medium">
										{formatDateOnly(new Date(equipment.createdAt).getTime())}
									</dd>
								</div>
							</dl>
						</CardContent>
					</Card>
				</TabsContent>

				<TabsContent value="images" className="mt-4">
					<MediaGallery
						orgId={orgId}
						entityType="EquipMgmtEquipment"
						entityId={equipmentId}
					/>
				</TabsContent>
			</Tabs>
		</div>
	);
}
